import { getUpdatedOrders, validateOrder } from '../shared/utils'
import { FiatOrderSet } from '@exodus/models'

const channelConfig = {
  type: 'fiatOrder',
  channelName: 'fiatOrders',
  syncStateKey: 'sync:syncstate:fiatorders',
  batchInterval: 1000,
  migration: '20.9.24',
}

class FiatOrders {
  #assetsModule
  #channel
  #fiatOrdersAtom
  #logger

  constructor({ assetsModule, fiatOrdersAtom, fusion, logger }) {
    this.#assetsModule = assetsModule
    this.#channel = fusion.channel({ ...channelConfig, processBatch: this.#processBatch })
    this.#fiatOrdersAtom = fiatOrdersAtom
    this.#logger = logger
  }

  #getChannel = async () => {
    await this.#channel.awaitProcessed()
    return this.#channel
  }

  #processBatch = async (orders) => {
    const assets = this.#assetsModule.getAssets()
    const existingOrders = await this.#fiatOrdersAtom.get()
    let newOrders = FiatOrderSet.fromArray([])
    const invalid = []
    let existingCount = 0
    for (const order of orders) {
      try {
        const validatedOrder = validateOrder(order.data, existingOrders, assets)
        if (validatedOrder) {
          newOrders = newOrders.add(validatedOrder)
        } else {
          existingCount++
        }
      } catch (error) {
        invalid.push({ error, order })
      }
    }

    if (invalid.length > 0) {
      this.#logger.warn('Skipped syncing invalid fiat orders', invalid)
    }

    if (existingCount) {
      this.#logger.debug(`Skipped syncing ${existingCount} existing fiat orders`)
    }

    if (newOrders.size === 0) return

    const updatedOrders = getUpdatedOrders(newOrders, existingOrders)
    if (updatedOrders) {
      await this.#fiatOrdersAtom.set(updatedOrders)
    }

    this.#logger.debug('Fiat orders synced from Fusion')
  }

  sync = async (order) => {
    const [channel, existingOrders] = await Promise.all([
      this.#getChannel(),
      this.#fiatOrdersAtom.get(),
    ])

    try {
      await Promise.all([
        channel.push({ data: order, type: channelConfig.type }),
        this.#fiatOrdersAtom.set(existingOrders.add(order)),
      ])

      this.#logger.debug('Synced fiat order to Fusion')
    } catch (error) {
      this.#logger.error('Failed to sync fiat order', order.toJSON(), error)
      throw error
    }
  }

  clear = async () => {
    await this.#fiatOrdersAtom.set(undefined)
  }
}

const fiatOrdersDefinition = {
  id: 'fiatOrders',
  type: 'module',
  factory: (...args) => new FiatOrders(...args),
  dependencies: ['assetsModule', 'fiatOrdersAtom', 'fusion', 'logger'],
  public: true,
}

export default fiatOrdersDefinition
